import { Injectable } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { Observable, catchError, filter, from, map, mergeMap, of, withLatestFrom } from "rxjs";
import { AnalyticsService } from "../../services/api";
import { analyticsRange } from "../../utils/analytics-range/analytics-range";
import * as fromProgram from "../program/selectors";
import * as analyticsActions from "./actions";
import * as fromAnalytics from "./selectors";
import { AnalyticsFilters, AnalyticsSection, AnalyticsSections } from "./state";

type SectionData = AnalyticsSections[AnalyticsSection]["data"];

@Injectable()
export class AnalyticsEffects {
  public readonly loadSections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(analyticsActions.loadSections),
      withLatestFrom(this.store.select(fromProgram.selectProgram), this.store.select(fromAnalytics.selectFilters)),
      filter(([, program]) => !!program?.id),
      mergeMap(([{ sections, preset }, program, filters]) =>
        from(sections).pipe(
          mergeMap(section =>
            this.request(section, program.id, { ...filters, preset: preset ?? filters.preset }).pipe(
              map(data => analyticsActions.loadSectionSuccess({ section, data, preset: preset ?? filters.preset })),
              catchError((error: HttpErrorResponse) => of(analyticsActions.loadSectionFailure({ section, error: error.message }))),
            ),
          ),
        ),
      ),
    ),
  );

  public readonly loadCampaignDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(analyticsActions.loadCampaignDetail),
      withLatestFrom(this.store.select(fromProgram.selectProgram)),
      filter(([, program]) => !!program?.id),
      mergeMap(([{ campaignId }, program]) =>
        // The API picks the window per campaign type (72 h since sending for notifications) and benchmarks it against the program's average
        this.analyticsService.analyticsControllerGetCampaignDetailAnalyticsV1({ programId: program.id, campaignId }).pipe(
          map(data => analyticsActions.loadCampaignDetailSuccess({ campaignId, data })),
          catchError((error: HttpErrorResponse) => of(analyticsActions.loadCampaignDetailFailure({ campaignId, error: error.message }))),
        ),
      ),
    ),
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly analyticsService: AnalyticsService,
  ) {}

  private request(section: AnalyticsSection, programId: string, filters: AnalyticsFilters): Observable<SectionData> {
    const params = {
      programId,
      ...analyticsRange(filters.preset),
      compare: filters.compare,
      ...(filters.granularity ? { granularity: filters.granularity } : {}),
    };

    switch (section) {
      case "overview": return this.analyticsService.analyticsControllerGetOverviewAnalyticsV1(params);
      case "insights": return this.analyticsService.analyticsControllerGetInsightsAnalyticsV1(params);
      case "segments": return this.analyticsService.analyticsControllerGetSegmentsAnalyticsV1({ programId });
      case "members": return this.analyticsService.analyticsControllerGetMembersAnalyticsV1(params);
      case "revenue": return this.analyticsService.analyticsControllerGetRevenueAnalyticsV1(params);
      case "cohorts": return this.analyticsService.analyticsControllerGetCohortsAnalyticsV1(params);
      case "points": return this.analyticsService.analyticsControllerGetPointsAnalyticsV1(params);
      case "tiers": return this.analyticsService.analyticsControllerGetTiersAnalyticsV1(params);
      case "rewards": return this.analyticsService.analyticsControllerGetRewardsAnalyticsV1(params);
      case "passes": return this.analyticsService.analyticsControllerGetPassesAnalyticsV1(params);
      case "acquisition": return this.analyticsService.analyticsControllerGetAcquisitionAnalyticsV1(params);
      case "campaigns": return this.analyticsService.analyticsControllerGetCampaignsAnalyticsV1({ ...params, limit: 20 });
    }
  }
}
